import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../hooks/wishlist';
import { useCart } from '../hooks/cart';
import { formatPrice } from '../hooks/generalHooks';
import SERVER from '../hooks/useServer';
import { FaHeart, FaShoppingBag, FaTrash } from 'react-icons/fa';
import { PiShoppingCartSimple } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const Wishlist = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { wishlist, removeFromWishlist, clearWishlist } = useWishlist();
    const { addToCart } = useCart();

    const handleClearWishlist = () => {
        const confirmClear = window.confirm(t('wishlist.clearConfirm'));
        if (confirmClear) {
            clearWishlist();
            toast.success(t('wishlist.cleared'));
        }
    };

    if (wishlist.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen py-12 px-4 pt-24">
                <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md w-full text-center">
                    <FaHeart className="text-gray-300 text-6xl mx-auto mb-6" />
                    <h1 className="text-2xl font-bold text-black mb-4">{t('wishlist.empty')}</h1>
                    <p className="text-gray-600 mb-8">
                        {t('wishlist.emptyDescription')}
                    </p>
                    <button 
                        onClick={() => navigate('/products')}
                        className="flex items-center justify-center gap-2 mx-auto bg-primary text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                        <FaShoppingBag size={18} />
                        {t('wishlist.discoverProducts')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-8 pt-24">
            {/* Page Title and Actions */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-black mb-2">{t('wishlist.title')}</h1>
                    <p className="text-gray-600">{wishlist.length} {wishlist.length > 1 ? t('wishlist.products') : t('wishlist.product')}</p>
                </div>
                
                {/* Clear Wishlist Button */}
                <button
                    onClick={handleClearWishlist}
                    className="flex items-center max-md:text-base gap-2 px-6 py-3 border-2 border-red-500 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors font-medium"
                >
                    <FaTrash size={16} />
                    {t('wishlist.clearList')}
                </button>
            </div>

            {/* Wishlist Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {wishlist.map((product) => (
                    <div 
                        key={product.id}
                        className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                    >
                        {/* Product Image */}
                        <div 
                            className="relative h-64 bg-gray-50 cursor-pointer"
                            onClick={() => {
                                navigate(`/product/${product.category_title}/${product.subcategory_title}/${product.title}`, {
                                    state: {
                                        categoryId: product.category_id,
                                        subCategoryId: product.subcategory_id,
                                        productId: product.id
                                    }
                                });
                            }}
                        >
                            <img 
                                src={`${SERVER}/${product.banner_image}`}
                                alt={product.title}
                                className="w-full h-full object-contain p-4"
                            />
                            
                            {/* Remove from Wishlist Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeFromWishlist(product.id);
                                }}
                                className="absolute top-4 right-4 bg-white p-2 rounded-full shadow-md hover:shadow-lg transition-all hover:scale-110"
                                title={t('wishlist.removeFromWishlist')}
                            >
                                <FaHeart className="text-red-500" size={20} />
                            </button>

                            {/* Badges */}
                            <div className="absolute top-4 left-4 flex flex-col gap-2">
                                {product.promotion > 0 && (
                                    <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">
                                        -{formatPrice(product.promotion, 0)}%
                                    </span>
                                )}
                                {product.nouveaute && (
                                    <span className="bg-yellow-400 text-black text-xs font-medium px-2 py-1 rounded">
                                        {t('wishlist.new')}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="p-4">
                            <h3 className="text-lg font-medium text-black mb-2 line-clamp-2 min-h-[56px]">
                                {product.title}
                            </h3>

                            {/* Price */}
                            <div className="mb-4">
                                {product.promotion > 0 ? (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-red-500">
                                            € {formatPrice(product.price * (1 - product.promotion / 100))}
                                        </span>
                                        <span className="text-sm text-gray-400 line-through">
                                            € {formatPrice(product.price)}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-xl font-bold text-primary">
                                        € {formatPrice(product.price)}
                                    </span>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                                {/* Add to Cart Button */}
                                <button
                                    onClick={() => addToCart(product, 1, 0)}
                                    className="flex-1 flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    <PiShoppingCartSimple size={20} />
                                    {t('wishlist.addToCart')}
                                </button>
                                
                                {/* Remove Button */}
                                <button
                                    onClick={() => removeFromWishlist(product.id)}
                                    className="flex items-center justify-center w-12 bg-red-50 text-red-500 py-3 rounded-lg hover:bg-red-100 transition-colors"
                                    title={t('wishlist.remove')}
                                >
                                    <FaTrash size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Wishlist;
